import React, { useState, useEffect } from 'react';
import { format, startOfMonth, endOfMonth, eachDayOfInterval, isSameMonth, isSameDay, addMonths, subMonths, addDays } from 'date-fns';
import { FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import { CURRENCY_SYMBOLS_TEXT } from '../utils/currencySymbols';

const CustomCalendar = ({ onDateClick, expenseData = {}, currentDate, setCurrentDate, recurringExpenses = [] }) => {
  const [calendarDays, setCalendarDays] = useState([]);

  useEffect(() => {
    const start = startOfMonth(currentDate);
    const end = endOfMonth(currentDate);
    
    // Get the first day of the month
    const firstDayOfMonth = start.getDay();
    
    // Get days from previous month to fill the first week
    const prevMonthDays = Array.from({ length: firstDayOfMonth }, (_, i) => {
      return subMonths(addDays(start, -firstDayOfMonth + i), 0);
    });
    
    // Get all days of current month
    const currentMonthDays = eachDayOfInterval({ start, end });
    
    // Get days from next month to fill the last week
    const lastDayOfMonth = end.getDay();
    const nextMonthDays = Array.from({ length: 6 - lastDayOfMonth }, (_, i) => {
      return addDays(end, i + 1);
    });
    
    // Combine all days
    const allDays = [...prevMonthDays, ...currentMonthDays, ...nextMonthDays];
    setCalendarDays(allDays);
  }, [currentDate]);

  const handlePrevMonth = () => setCurrentDate(subMonths(currentDate, 1));
  const handleNextMonth = () => setCurrentDate(addMonths(currentDate, 1));

  const renderExpensePreviews = (date) => {
    const dateKey = format(date, 'yyyy-MM-dd');
    const expenses = expenseData[dateKey] || [];
    
    // Find recurring expenses for this date
    const recurringForDate = recurringExpenses.filter(recurring => {
      if (recurring.isPaused) return false;
      
      // For one-off payments, only show on the exact date
      if (recurring.isOneOff) {
        return isSameDay(new Date(recurring.nextPaymentDate), new Date(dateKey));
      }
      
      // Get the start date and end date of the recurring expense
      const startDate = new Date(recurring.nextPaymentDate);
      startDate.setHours(0, 0, 0, 0); // Normalize to midnight
      const currentDate = new Date(dateKey);
      currentDate.setHours(0, 0, 0, 0); // Normalize to midnight
      const endDate = recurring.endDate ? new Date(recurring.endDate) : null;
      if (endDate) endDate.setHours(0, 0, 0, 0); // Normalize to midnight
      
      // If the current date is before the start date or after the end date, don't show it
      if (currentDate < startDate) return false;
      if (endDate && currentDate > endDate) return false;
      
      // Calculate if this date matches the recurring pattern
      const diffTime = currentDate.getTime() - startDate.getTime();
      const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
      
      // Normalize the period unit to handle both singular and plural forms
      const normalizedUnit = recurring.period.unit.toLowerCase().replace(/s$/, '');
      
      switch (normalizedUnit) {
        case 'day':
          return diffDays % parseInt(recurring.period.number) === 0;
        case 'week':
          return diffDays % (parseInt(recurring.period.number) * 7) === 0;
        case 'month':
          return currentDate.getDate() === startDate.getDate() &&
                 (currentDate.getMonth() - startDate.getMonth() + 
                  (currentDate.getFullYear() - startDate.getFullYear()) * 12) % 
                  parseInt(recurring.period.number) === 0;
        case 'year':
          return currentDate.getDate() === startDate.getDate() &&
                 currentDate.getMonth() === startDate.getMonth() &&
                 (currentDate.getFullYear() - startDate.getFullYear()) % 
                 parseInt(recurring.period.number) === 0;
        default:
          return false;
      }
    });
    
    if (expenses.length === 0 && recurringForDate.length === 0) return null;

    // Combine and sort all expenses by amount
    const allExpenses = [
      ...expenses,
      ...recurringForDate.map(rec => ({
        ...rec,
        isRecurring: true
      }))
    ].sort((a, b) => parseFloat(b.amount) - parseFloat(a.amount));

    return (
      <div className="hidden sm:block expense-previews text-xs space-y-1">
        {allExpenses.slice(0, 4).map((expense, index) => (
          <div 
            key={`${expense.id}-${index}`}
            className={`truncate ${expense.isRecurring ? 'text-teal-600 dark:text-pink-300 font-bold' : 'text-gray-600'}`}
          >
            {`${CURRENCY_SYMBOLS_TEXT[expense.currency]}${expense.amount} - ${expense.title}`}
          </div>
        ))}
        {allExpenses.length > 4 && (
          <div className="text-gray-500 italic">
            +{allExpenses.length - 4} more
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="bg-white rounded-lg shadow p-2">
      {/* Calendar Header */}
      <div className="flex items-center justify-between mb-4 sm:mb-6">
        <button
          onClick={handlePrevMonth}
          className="p-2 hover:bg-gray-100 rounded-full"
        >
          <FaChevronLeft className="w-5 h-5 text-gray-600" />
        </button>
        <h2 className="text-xl font-semibold text-gray-900">
          {format(currentDate, 'MMMM yyyy')}
        </h2>
        <button
          onClick={handleNextMonth}
          className="p-2 hover:bg-gray-100 rounded-full"
        >
          <FaChevronRight className="w-5 h-5 text-gray-600" />
        </button>
      </div>

      {/* Calendar Grid */}
      <div className="grid grid-cols-7 gap-0.5 sm:gap-1">
        {/* Weekday headers */}
        {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day) => (
          <div
            key={day}
            className="text-center font-semibold text-gray-600 p-1 sm:p-2 text-xs sm:text-base"
          >
            {day}
          </div>
        ))}

        {/* Calendar days */}
        {calendarDays.map((date, index) => {
          const isCurrentMonth = isSameMonth(date, currentDate);
          return (
            <div
              key={index}
              onClick={isCurrentMonth ? () => onDateClick(date) : undefined}
              className={`
                aspect-square sm:aspect-auto sm:min-h-[150px] p-1 sm:p-2 border rounded-lg transition-colors duration-200
                ${isCurrentMonth 
                  ? 'cursor-pointer hover:bg-gray-200 hover:border-yellow-200 dark:hover:bg-teal-700' 
                  : 'cursor-not-allowed bg-gray-100 dark:bg-gray-900 text-gray-400 dark:text-gray-600'}
                ${isSameDay(date, new Date()) && isSameMonth(date, new Date())
                  ? 'border-indigo-500 bg-yellow-200 dark:bg-gradient-to-br from-indigo-700 via-purple-700 to-pink-700' 
                  : 'border-gray-200'}
                border-2
                dark:border-gray-600
              `}
            >
              <div className="text-right mb-1 text-xs sm:text-base">
                {format(date, 'd')}
              </div>
              {isCurrentMonth && renderExpensePreviews(date)}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default CustomCalendar;
